/**
 * Memory monitoring and pressure detection for mobile devices
 */

export interface MemoryInfo {
  usedJSHeapSize?: number;
  totalJSHeapSize?: number;
  jsHeapSizeLimit?: number;
  deviceMemory?: number;
}

export type MemoryPressureLevel = 'normal' | 'moderate' | 'critical';

/**
 * Get current memory information from the browser
 */
export const getMemoryInfo = (): MemoryInfo => {
  const memory: MemoryInfo = {};
  
  // Chrome/Edge provides performance.memory
  if ('memory' in performance) {
    const perfMemory = (performance as any).memory;
    memory.usedJSHeapSize = perfMemory.usedJSHeapSize;
    memory.totalJSHeapSize = perfMemory.totalJSHeapSize;
    memory.jsHeapSizeLimit = perfMemory.jsHeapSizeLimit;
  }
  
  // Device memory hint (in GB)
  if ('deviceMemory' in navigator) {
    memory.deviceMemory = (navigator as any).deviceMemory;
  }
  
  return memory;
};

/**
 * Calculate current memory pressure level
 */
export const getMemoryPressure = (): MemoryPressureLevel => {
  const memory = getMemoryInfo();
  
  // If we have heap size info, use it
  if (memory.usedJSHeapSize && memory.jsHeapSizeLimit) {
    const usagePercent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
    
    if (usagePercent > 85) return 'critical';
    if (usagePercent > 70) return 'moderate';
    return 'normal';
  }
  
  // Fallback to device memory hint
  if (memory.deviceMemory) {
    // Less than 2GB = critical, 2-4GB = moderate, 4GB+ = normal
    if (memory.deviceMemory < 2) return 'critical';
    if (memory.deviceMemory < 4) return 'moderate';
    return 'normal';
  }
  
  // No memory info available, assume moderate
  return 'moderate';
};

/**
 * Get recommended image quality based on memory pressure
 */
export const getRecommendedImageQuality = (): number => {
  const pressure = getMemoryPressure();
  
  switch (pressure) {
    case 'critical':
      return 0.6; // 60% quality
    case 'moderate':
      return 0.75; // 75% quality
    case 'normal':
    default:
      return 0.85; // 85% quality
  }
};

/**
 * Get recommended max image dimensions based on memory pressure
 */
export const getRecommendedMaxDimensions = (): { width: number; height: number } => {
  const pressure = getMemoryPressure();
  
  switch (pressure) {
    case 'critical':
      return { width: 512, height: 512 };
    case 'moderate':
      return { width: 768, height: 768 };
    case 'normal':
    default:
      return { width: 1024, height: 1024 };
  }
};

/**
 * Check if we should enable low-memory mode
 */
export const shouldUseLowMemoryMode = (): boolean => {
  return getMemoryPressure() !== 'normal';
};

/**
 * Log memory stats to console (debug only)
 */
export const logMemoryStats = () => {
  const memory = getMemoryInfo();
  const pressure = getMemoryPressure();
  
  console.log('=== Memory Stats ===');
  console.log('Pressure Level:', pressure);
  
  if (memory.usedJSHeapSize && memory.jsHeapSizeLimit) {
    const usageMB = (memory.usedJSHeapSize / 1024 / 1024).toFixed(2);
    const limitMB = (memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2);
    const percent = ((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100).toFixed(1);
    console.log(`Heap: ${usageMB}MB / ${limitMB}MB (${percent}%)`);
  }
  
  if (memory.deviceMemory) {
    console.log(`Device Memory: ${memory.deviceMemory}GB`);
  }
  
  console.log('Recommended Quality:', getRecommendedImageQuality());
  console.log('Recommended Max Dimensions:', getRecommendedMaxDimensions());
  console.log('==================');
};
